import { useCallback, useEffect, useState } from 'react';
import { apiFetch, apiFetchBlob } from '@/lib/supabaseClient';

export function useHaccpConfigurations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/haccp/configuration');
      if (!response || !Array.isArray(response.locations)) {
        const contractError = new Error('La risposta delle configurazioni non è valida.');
        contractError.code = 'HACCP_CONFIGURATION_INVALID_RESPONSE';
        throw contractError;
      }
      setLocations(response.locations);
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { locations, loading, error, reload };
}

export async function createHaccpDraft(locationId) {
  return apiFetch(`/api/haccp/configuration/${encodeURIComponent(locationId)}/draft`, {
    method: 'POST',
  });
}

export async function saveHaccpConfiguration(locationId, configurationId, expectedRevision, modules, equipment) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}`,
    {
    method: 'PUT',
    body: JSON.stringify({
      expected_revision: expectedRevision,
      selected_modules: modules,
      equipment: equipment.map(({ id, kind, number, label }) => ({
        ...(id ? { id } : {}),
        kind,
        number,
        label: label || null,
      })),
    }),
    },
  );
}

export async function submitHaccpConfiguration(locationId, configurationId, expectedRevision, note = null) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/submit`,
    {
      method: 'POST',
      body: JSON.stringify({ expected_revision: expectedRevision, note }),
    },
  );
}

export async function approveHaccpConfiguration(locationId, configurationId, expectedRevision, note = null) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({ expected_revision: expectedRevision, note }),
    },
  );
}

export async function returnHaccpConfiguration(locationId, configurationId, expectedRevision, note = null) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/return`,
    {
      method: 'POST',
      body: JSON.stringify({ expected_revision: expectedRevision, note }),
    },
  );
}

export async function getHaccpTemperatureLocations() {
  return apiFetch('/api/haccp/temperature/locations');
}

export async function getHaccpConfigurationLocations() {
  return apiFetch('/api/haccp/configuration');
}

export async function getHaccpTemperatureSummary(locationId) {
  return apiFetch(`/api/haccp/temperature/summary?location_id=${encodeURIComponent(locationId)}`);
}

export async function getHaccpTemperatureHistory(locationId, dateFrom, dateTo) {
  const params = new URLSearchParams({
    location_id: locationId,
    date_from: dateFrom,
    date_to: dateTo,
  });
  return apiFetch(`/api/haccp/temperature/history?${params.toString()}`);
}

export async function recordHaccpTemperature(payload) {
  return apiFetch('/api/haccp/temperature/checks', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createHaccpTemperatureStation(payload) {
  return apiFetch('/api/haccp/temperature/stations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function reviseHaccpTemperatureStation(stationId, locationId, payload) {
  return apiFetch(
    `/api/haccp/temperature/stations/${encodeURIComponent(stationId)}/settings?location_id=${encodeURIComponent(locationId)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
}

export async function acquireHaccpTemperaturePhoto(
  locationId, equipmentId, file, requestId, standalone = null,
) {
  const body = new FormData();
  body.append('location_id', locationId);
  if (standalone?.station_id) {
    body.append('station_id', standalone.station_id);
    body.append('profile_revision_id', standalone.profile_revision_id);
  } else {
    body.append('equipment_id', equipmentId);
  }
  body.append('request_id', requestId);
  body.append('photo', file);
  return apiFetch('/api/haccp/temperature/captures', { method: 'POST', body });
}

export const getHaccpTemperaturePhoto = apiFetchBlob;

export async function getHaccpTemperatureRules(locationId, configurationId) {
  return apiFetch(
    `/api/haccp/temperature/configuration/${encodeURIComponent(locationId)}/${encodeURIComponent(configurationId)}/rules`,
  );
}

export async function saveHaccpTemperatureRules(locationId, configurationId, expectedRevision, rules) {
  return apiFetch(
    `/api/haccp/temperature/configuration/${encodeURIComponent(locationId)}/${encodeURIComponent(configurationId)}/rules`,
    {
      method: 'PUT',
      body: JSON.stringify({ expected_revision: expectedRevision, rules }),
    },
  );
}

export async function getHaccpTemperatureTemplates(locationId) {
  return apiFetch(
    `/api/haccp/temperature/configuration/${encodeURIComponent(locationId)}/templates`,
  );
}

export async function createHaccpTemperatureTemplate(locationId, template) {
  return apiFetch(
    `/api/haccp/temperature/configuration/${encodeURIComponent(locationId)}/templates`,
    { method: 'POST', body: JSON.stringify(template) },
  );
}

export async function updateHaccpTemperatureTemplate(locationId, templateId, template) {
  return apiFetch(
    `/api/haccp/temperature/configuration/${encodeURIComponent(locationId)}/templates/${encodeURIComponent(templateId)}`,
    { method: 'PUT', body: JSON.stringify(template) },
  );
}

export async function getHaccpPositiveRules(locationId, configurationId) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/blast-chilling-positive-rules`,
  );
}

export async function saveHaccpPositiveRules(locationId, configurationId, expectedRevision, rules) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/blast-chilling-positive-rules`,
    {
      method: 'PUT',
      body: JSON.stringify({ expected_revision: expectedRevision, rules }),
    },
  );
}

export async function getHaccpPositiveContext(locationId) {
  return apiFetch(
    `/api/haccp/blast-chilling/positive/context?location_id=${encodeURIComponent(locationId)}`,
  );
}

export async function startHaccpPositiveCycle(payload) {
  return apiFetch('/api/haccp/blast-chilling/positive/cycles', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function recordHaccpPositiveMeasurement(cycleId, locationId, payload) {
  return apiFetch(
    `/api/haccp/blast-chilling/positive/cycles/${encodeURIComponent(cycleId)}/measurements?location_id=${encodeURIComponent(locationId)}`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function completeHaccpPositiveCycle(cycleId, locationId, payload) {
  return apiFetch(
    `/api/haccp/blast-chilling/positive/cycles/${encodeURIComponent(cycleId)}/complete?location_id=${encodeURIComponent(locationId)}`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function getHaccpPositiveCycles(locationId, limit = 50, offset = 0) {
  const params = new URLSearchParams({
    location_id: locationId,
    limit: String(limit),
    offset: String(offset),
  });
  return apiFetch(`/api/haccp/blast-chilling/positive/cycles?${params.toString()}`);
}

export async function getHaccpPositiveCurrentCycle(locationId) {
  return apiFetch(
    `/api/haccp/blast-chilling/positive/cycles/current?location_id=${encodeURIComponent(locationId)}`,
  );
}

export async function getHaccpPositiveCycle(cycleId, locationId) {
  return apiFetch(
    `/api/haccp/blast-chilling/positive/cycles/${encodeURIComponent(cycleId)}?location_id=${encodeURIComponent(locationId)}`,
  );
}

export async function acquireHaccpPositivePhoto(cycleId, locationId, phaseId, file, requestId) {
  const body = new FormData();
  body.append('location_id', locationId);
  body.append('phase_id', phaseId);
  body.append('request_id', requestId);
  body.append('photo', file);
  return apiFetch(
    `/api/haccp/blast-chilling/positive/cycles/${encodeURIComponent(cycleId)}/captures`,
    { method: 'POST', body },
  );
}

export const getHaccpPositivePhoto = apiFetchBlob;

export async function getHaccpNegativeRules(locationId, configurationId) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/blast-chilling-negative-rules`,
  );
}

export async function saveHaccpNegativeRules(locationId, configurationId, expectedRevision, rules) {
  return apiFetch(
    `/api/haccp/configuration/${encodeURIComponent(locationId)}/draft/${encodeURIComponent(configurationId)}/blast-chilling-negative-rules`,
    { method: 'PUT', body: JSON.stringify({ expected_revision: expectedRevision, rules }) },
  );
}

export async function getHaccpNegativeContext(locationId) {
  return apiFetch(`/api/haccp/blast-chilling/negative/context?location_id=${encodeURIComponent(locationId)}`);
}

export async function startHaccpNegativeCycle(payload) {
  return apiFetch('/api/haccp/blast-chilling/negative/cycles', { method: 'POST', body: JSON.stringify(payload) });
}

export async function recordHaccpNegativeMeasurement(cycleId, locationId, payload) {
  return apiFetch(
    `/api/haccp/blast-chilling/negative/cycles/${encodeURIComponent(cycleId)}/measurements?location_id=${encodeURIComponent(locationId)}`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function completeHaccpNegativeCycle(cycleId, locationId, payload) {
  return apiFetch(
    `/api/haccp/blast-chilling/negative/cycles/${encodeURIComponent(cycleId)}/complete?location_id=${encodeURIComponent(locationId)}`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function getHaccpNegativeCycles(locationId, limit = 50, offset = 0) {
  const params = new URLSearchParams({ location_id: locationId, limit: String(limit), offset: String(offset) });
  return apiFetch(`/api/haccp/blast-chilling/negative/cycles?${params.toString()}`);
}

export async function getHaccpNegativeCurrentCycle(locationId) {
  return apiFetch(`/api/haccp/blast-chilling/negative/cycles/current?location_id=${encodeURIComponent(locationId)}`);
}

export async function getHaccpNegativeCycle(cycleId, locationId) {
  return apiFetch(
    `/api/haccp/blast-chilling/negative/cycles/${encodeURIComponent(cycleId)}?location_id=${encodeURIComponent(locationId)}`,
  );
}

export async function acquireHaccpNegativePhoto(cycleId, locationId, phaseId, file, requestId) {
  const body = new FormData();
  body.append('location_id', locationId);
  body.append('phase_id', phaseId);
  body.append('request_id', requestId);
  body.append('photo', file);
  return apiFetch(`/api/haccp/blast-chilling/negative/cycles/${encodeURIComponent(cycleId)}/captures`, {
    method: 'POST',
    body,
  });
}
